import type { BotConversation } from '../../types/leadBot';

// What the fitness bot has learned about a lead, and where the follow-ups stand. Renders
// NOTHING for an automotive conversation (no lead facts, no schedule). The facts come from the
// reply call that already happens (no extra AI call), and they are the lead's own words, so
// they are shown as quoted context, never as the club's claims.

function when(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

export function FitnessSummary({ conversation }: { conversation: BotConversation }) {
  const facts = conversation.leadFacts ?? null;
  const rows: Array<[string, string]> = facts
    ? ([
        ['Goal', facts.goal],
        ['Why now', facts.motivation],
        ['Times that suit', facts.preferredTimes],
        ['Hesitations', facts.objections],
        ['Readiness', facts.readiness],
      ] as Array<[string, string]>).filter(([, v]) => v && v.trim() !== '')
    : [];

  const due = conversation.nextFollowUpAt ?? null;
  const skips = conversation.followUpSkips ?? [];
  const finished = conversation.followUpsFinishedAt ?? null;
  const nothing = rows.length === 0 && !due && skips.length === 0 && !finished && !conversation.outsideStaffedHours;
  if (nothing) return null;

  return (
    <div className="mt-2 space-y-1 rounded-md border bg-slate-50 p-3 text-xs text-slate-700">
      {rows.map(([label, value]) => (
        <p key={label}>
          <span className="font-medium">{label}:</span> {value}
        </p>
      ))}
      {conversation.outsideStaffedHours ? <p>Arrived outside staffed hours.</p> : null}
      {due ? (
        <p>
          <span className="font-medium">Next follow-up:</span>{' '}
          {conversation.followUpSequence === 'no_response' ? 'No response' : 'Conversation incomplete'} step{' '}
          {(conversation.followUpStep ?? 0) + 1}, due {when(due)}
        </p>
      ) : null}
      {finished ? <p>Follow-ups finished {when(finished)}. A person decides the outcome.</p> : null}
      {skips.map((s, i) => (
        <p key={i} className="text-amber-800">
          Follow-up step {s.step + 1} did not send: {s.reason}
        </p>
      ))}
    </div>
  );
}
